import { twMerge } from 'tailwind-merge';
import { useMemo, useState, useEffect } from 'react';

import { CourseStepStatus } from 'types';
import { getStepData } from 'utils/steps';
import { useCourseContext } from 'context';

import { StepButtonProps } from './types';

export const StepButton = ({ item, onClick, showDivider }: StepButtonProps) => {
  const [isHovered, setHovered] = useState(false);
  const { getStepStatus } = useCourseContext();

  const { id, name, color, codeName, coverImage, resourceVersions } = item;
  const { backgroundLight } = useMemo(
    () => getStepData(color, codeName),
    [color, codeName]
  );

  const status = useMemo(() => getStepStatus(id), [getStepStatus, id]);

  const isResource = resourceVersions && resourceVersions.length > 0;

  const isActive = status === CourseStepStatus.Active;
  const isCompleted = status === CourseStepStatus.Completed;
  const isUnlocked = isActive || isCompleted;

  const isClickable = isActive || (isResource && isCompleted);

  useEffect(() => {
    if (!isClickable && isHovered) {
      setHovered(false);
    }
  }, [isClickable, isHovered]);

  const handleClick = isClickable ? onClick : undefined;
  const handleMouseEnter = isClickable ? () => setHovered(true) : undefined;
  const handleMouseLeave = isClickable ? () => setHovered(false) : undefined;

  return (
    <>
      {showDivider && (
        <div className="bg-gray-primary h-px flex-1 self-center md:w-4" />
      )}
      <div className="flex items-center">
        <div
          onClick={handleClick}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          className={twMerge(
            'relative flex h-5 w-5 items-center justify-center rounded-md md:h-7 md:w-7 md:rounded-lg',
            isClickable && 'cursor-pointer'
          )}
        >
          <div
            style={{
              background: isUnlocked ? color : backgroundLight,
            }}
            className={twMerge(
              'absolute inset-0 rounded-[inherit] transition-opacity',
              isHovered && 'opacity-80'
            )}
          />
          <img
            alt={name}
            src={coverImage}
            className={twMerge(
              'relative h-3 w-3 md:h-4 md:w-4',
              isUnlocked && 'brightness-0 invert'
            )}
          />
        </div>
      </div>
    </>
  );
};
